// src/activities/cape-vr/cape-vr-views/CAPEVRForm.jsx

// TODO Fer anar useCapevrFormState igual que a CapeVrTool?

import { useState, useEffect } from 'react';
import { useAuth } from '../../../auth/authContext';
import { useUserData } from '../../../hooks/useUserData';
import CAPEVrProtocolLink from './cape-vr-files/cape-vr-form-files/CAPEVrProtocolLink';
import SharingConsentModal from '../../../components/modals/app-modals/SharingConsentModal';
import axios from 'axios';
import { useFixedTForResource } from '../../../locales/useFixedTForResource';
import ResourceLanguageWarning from '../../../locales/ResourceLanguageWarning';
import AVIcon from '../../../components/icons/AllVoicedIcons';
import Demographics from './cape-vr-files/cape-vr-form-files/Demographics';
import RecordingConditions from './cape-vr-files/cape-vr-form-files/RecordingConditions';
import VocalTasks from './cape-vr-files/cape-vr-form-files/VocalTasks';
import RatingConditions from './cape-vr-files/cape-vr-form-files/RatingConditions';
import CAPEVRRatingForm from './cape-vr-files/cape-vr-form-files/CAPEVRRatingForm';
import resolveAudioUrl from '../../../utils/audio/resolveAudioUrl';
import { HANDMADE_EVALUATION_TOOL_IDS } from '../../../constants/handmadeEvaluationTools';
import {
  buildCapeVrNormalizedAssessment,
  capeVrSampleTaskProvenance,
  validateCapeVrState,
} from '../capeVrResponseContract';
import { capeVrValidationMessages } from '../capeVrValidationPresentation';

function CAPEVRForm({ apiUrl, selectedSample, sampleDatabase, onSubmit, defaultPerceptualData, consensusNumericPrefill, isConsensusMode, useCase, renderSample, sampleTaskProvenance }) {
  const t = useFixedTForResource('CAPEVR');
  const { user } = useAuth();
  const { sociodemographicData } = useUserData(apiUrl, user);
  const audioSrc = resolveAudioUrl(selectedSample?.sampleLink);

  // 🔹 Estat per gestionar l'obertura del SharingConsentModal
  const [isSharingConsentModalOpen, setIsSharingConsentModalOpen] = useState(false);
  const [pendingSharedAssessment, setPendingSharedAssessment] = useState(null);
  const [validationSnapshot, setValidationSnapshot] = useState(null);

  // Destructurem les dades de l'usuari autenticat per fer-les servir a `raterData`
  const {
    age: raterAge,
    pronoun: raterPronoun,
    countryOfResidence: raterCountry,
    communicationLanguage: raterLanguage,
    profession: raterProfession,
    voiceCompetenceLevel: raterVoiceCompetence,
    wantsToShareEvaluations, 
    acceptResearchUsage, 
  } = sociodemographicData || {};

  // Estats per a Demographics
  const [subjectNameId, setSubjectNameId] = useState('');
  const [subjectGender, setSubjectGender] = useState('');
  const [subjectAge, setSubjectAge] = useState('');
  const [examinerId, setExaminerId] = useState('');
  const [recordingDate, setRecordingDate] = useState('');

  // Estats per a RecordingConditions
  const [isAudioRecorded, setIsAudioRecorded] = useState('');
  const [sessionModality, setSessionModality] = useState('');
  const [recordingEnvironment, setRecordingEnvironment] = useState('');
  const [recordingSetup, setRecordingSetup] = useState('');
  const [mouthToMicDistanceCm, setMouthToMicDistanceCm] = useState('');

  // Estats per a CAPEVRVocalTasks
  const [areSentencesModeled, setAreSentencesModeled] = useState(false);
  const [readingPassage, setReadingPassage] = useState('');

  // Estats per a RatingConditions
  const [voiceSource, setVoiceSource] = useState('');
  const [listeningDevice, setListeningDevice] = useState('');
  const [auditoryAnchors, setAuditoryAnchors] = useState('');
  const [raterId, setRaterId] = useState('');
  const [ratingDate, setRatingDate] = useState('');
  const [timesPlayed, setTimesPlayed] = useState('');

  // Estats per a StandardVocalAttributesRating
  const [overallSeverity, setOverallSeverity] = useState(
    () => consensusNumericPrefill?.overallSeverity ?? defaultPerceptualData?.overallSeverity?.VASValue ?? null
  );
  const [roughness, setRoughness] = useState(
    () => consensusNumericPrefill?.roughness ?? defaultPerceptualData?.roughness?.VASValue ?? null
  );
  const [breathiness, setBreathiness] = useState(
    () => consensusNumericPrefill?.breathiness ?? defaultPerceptualData?.breathiness?.VASValue ?? null
  );
  const [strain, setStrain] = useState(
    () => consensusNumericPrefill?.strain ?? defaultPerceptualData?.strain?.VASValue ?? null
  );
  const [customVasLabel, setCustomVasLabel] = useState('');
  const [customVasValue, setCustomVasValue] = useState(null);

  // Estats per a OtherVocalAttributesRating 
  const [instabilitiesValues, setInstabilitiesValues] = useState(() => defaultPerceptualData?.instabilitiesValues || {
    otherInstability: { attributeName: '', VASValue: null }
  });
  const [additionalFeaturesValues, setAdditionalFeaturesValues] = useState(() => defaultPerceptualData?.additionalFeaturesValues || {
    otherAdditionalFeature: { attributeName: '', VASValue: null }
  });

  // Estats per a Pitch
  const [normalPitch, setNormalPitch] = useState(() => defaultPerceptualData?.normalPitch || { isPresent: false, attributeComment: '' });
  const [tooHighPitch, setTooHighPitch] = useState(() => defaultPerceptualData?.tooHighPitch || { isPresent: false, attributeComment: '' });
  const [tooLowPitch, setTooLowPitch] = useState(() => defaultPerceptualData?.tooLowPitch || { isPresent: false, attributeComment: '' });

  // Estats per a Loudness
  const [normalLoudness, setNormalLoudness] = useState(() => defaultPerceptualData?.normalLoudness || { isPresent: false, attributeComment: '' });
  const [tooLoudLoudness, setTooLoudLoudness] = useState(() => defaultPerceptualData?.tooLoudLoudness || { isPresent: false, attributeComment: '' });
  const [tooQuietLoudness, setTooQuietLoudness] = useState(() => defaultPerceptualData?.tooQuietLoudness || { isPresent: false, attributeComment: '' });

  // Estats per a ResonanceNasalityEvaluation
  const [normalResonance, setNormalResonance] = useState(() => defaultPerceptualData?.normalResonance || { isPresent: false, attributeComment: '' });
  const [frontResonance, setFrontResonance] = useState(() => defaultPerceptualData?.frontResonance || { isPresent: false, attributeComment: '' });
  const [backResonance, setBackResonance] = useState(() => defaultPerceptualData?.backResonance || { isPresent: false, attributeComment: '' });
  const [normalNasality, setNormalNasality] = useState(() => defaultPerceptualData?.normalNasality || { isPresent: false, attributeComment: '' });
  const [hypernasality, setHypernasality] = useState(() => defaultPerceptualData?.hypernasality || { isPresent: false, attributeComment: '' });
  const [hyponasality, setHyponasality] = useState(() => defaultPerceptualData?.hyponasality || { isPresent: false, attributeComment: '' });

  // Estats per a InconsistenciesEvaluation
  const [vowelsInconsistency, setVowelsInconsistency] = useState(() => defaultPerceptualData?.vowelsInconsistency || { isPresent: false, attributeComment: '' });
  const [sentencesInconsistency, setSentencesInconsistency] = useState(() => defaultPerceptualData?.sentencesInconsistency || { isPresent: false, attributeComment: '' });
  const [extemporaneousSpeechInconsistency, setExtemporaneousSpeechInconsistency] = useState(() => defaultPerceptualData?.extemporaneousSpeechInconsistency || { isPresent: false, attributeComment: '' });
  const [inconsistencyStatus, setInconsistencyStatus] = useState('unanswered');

  // Estats per a InstabilitiesEvaluation
  const [selectedInstabilities, setSelectedInstabilities] = useState([]);

  // Estats per a AdditionalFeaturesEvaluation
  const [selectedAdditionalFeatures, setSelectedAdditionalFeatures] = useState([]);

  // Estat per a OverallImpression
  const [overallImpressionComment, setOverallImpressionComment] = useState(() => defaultPerceptualData?.overallImpression || '');

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setRatingDate(today);
  }, []); 

  useEffect(() => {
    if (sampleDatabase !== 'upload' && selectedSample) {

        setSubjectNameId(selectedSample.subjectId?.subjectNameId || '');
        setSubjectGender(selectedSample.subjectId?.subjectProfile?.gender || '');
        setSubjectAge(selectedSample.subjectId?.subjectProfile?.age || '');
        setExaminerId(selectedSample.identificationData?.examinerId || '');
        setRecordingDate(selectedSample.identificationData?.recordingDate || '');

        if (selectedSample.identificationData?.voiceDatabaseId?.recordingConditions) {
            const rc = selectedSample.identificationData.voiceDatabaseId.recordingConditions;
            setIsAudioRecorded(rc.isAudioRecorded ? "yes" : "no");
            setSessionModality(rc.sessionModality || '');
            setRecordingEnvironment(rc.recordingEnvironment || '');
            setRecordingSetup(rc.recordingSetup || '');
            setMouthToMicDistanceCm(rc.mouthToMicDistanceCm || '');
        }

        setVoiceSource("recordedVoice");
    }
}, [selectedSample, sampleDatabase]);

useEffect(() => {
  if (defaultPerceptualData && isConsensusMode) {
    // Inicialitza selectedAdditionalFeatures
    const initialAdditional = Object.entries(defaultPerceptualData)
      .filter(([key, val]) =>
        ['aphonia', 'asthenia', 'diplophonia', 'falsetto', 'fry', 'hardGlottalAttack', 'wetGurgly', 'otherAdditionalFeature'].includes(key)
        && val?.isPresent === true
      )
      .map(([key]) => key);
    setSelectedAdditionalFeatures(initialAdditional);

    // Inicialitza selectedInstabilities
    const initialInstabilities = Object.entries(defaultPerceptualData)
      .filter(([key, val]) =>
        ['aphonicBreak', 'pitchBreak', 'pitchInstability', 'spasm', 'tremor', 'otherInstability'].includes(key)
        && val?.isPresent === true
      )
      .map(([key]) => key);
    setSelectedInstabilities(initialInstabilities);
  }
}, [defaultPerceptualData, isConsensusMode]);

  const currentResponseState = () => ({
    overallSeverity,
    roughness,
    breathiness,
    strain,
    customVasLabel,
    customVasValue,
    normalPitch,
    tooHighPitch,
    tooLowPitch,
    normalLoudness,
    tooLoudLoudness,
    tooQuietLoudness,
    normalResonance,
    frontResonance,
    backResonance,
    normalNasality,
    hypernasality,
    hyponasality,
    inconsistencyStatus,
    vowelsInconsistency,
    sentencesInconsistency,
    extemporaneousSpeechInconsistency,
    selectedInstabilities,
    instabilitiesValues,
    selectedAdditionalFeatures,
    additionalFeaturesValues,
    overallImpressionComment,
    taskProvenance: sampleTaskProvenance || capeVrSampleTaskProvenance(selectedSample),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    const responseState = currentResponseState();
    const validation = validateCapeVrState(responseState);
    if (!validation.valid) {
      setValidationSnapshot({ errors: validation.errors, messages: capeVrValidationMessages(validation.errors, t) });
      return;
    }
    setValidationSnapshot(null);

    const assessmentMetadata = {
      userId: sociodemographicData?.id || null,
      voiceSampleId: selectedSample?._id || null,
      voiceDatabaseId: selectedSample?.identificationData?.voiceDatabaseId || null,
      subjectId: selectedSample?.subjectId || null,
      isSharedForFeedback: false,

      raterData: {
        age: raterAge,
        pronoun: raterPronoun,
        countryOfResidence: raterCountry,
        communicationLanguage: raterLanguage,
        profession: raterProfession,
        voiceCompetenceLevel: raterVoiceCompetence,
      },

      ratingConditions: {
        voiceSource: voiceSource || null,
        listeningDevice: listeningDevice || null,
        auditoryAnchors: auditoryAnchors === "yes" ? true : auditoryAnchors === "no" ? false : null,
        ratingDate,
        timesPlayed,
      },

      evaluationTool: HANDMADE_EVALUATION_TOOL_IDS.CAPE_VR,
    };
    const voiceAssessment = buildCapeVrNormalizedAssessment({ state: responseState, assessmentMetadata });

    // 🔁 Nova lògica basada en useCase
    if (useCase === "evaluation") {
      console.log("🎯 useCase 'evaluation' detectat: no es mostra el modal, només update local.");
      onSubmit(voiceAssessment);
      return;
    }

    // 🔍 Només en training o interactiveSession es mostra el modal
    if (acceptResearchUsage || wantsToShareEvaluations) {
      setPendingSharedAssessment(voiceAssessment);
      setIsSharingConsentModalOpen(true);
    } else {
      onSubmit(voiceAssessment);
    }
  };
  
  const handleConfirmSharing = async ({ shareForFeedback, researchUsage }) => {
    if (!pendingSharedAssessment) {
      console.warn("⚠️ No hi ha dades pendents per compartir.");
      return;
    }
  
    // Si l'usuari no dóna consentiment, tanquem la modal i actualitzem la vista localment
    if (!shareForFeedback && !researchUsage) {
      console.log("L'usuari no dóna consentiment. No s'envia a la base de dades.");
      setIsSharingConsentModalOpen(false);
      onSubmit(pendingSharedAssessment); // Actualitza la vista (ratings/feedback) localment
      return;
    }
  
    const assessmentToSubmit = {
      ...pendingSharedAssessment,
      isSharedForFeedback: shareForFeedback,
      acceptResearchUsage: researchUsage,
    };
  
    console.log("📤 Dades enviades a /voice-assessments:", JSON.stringify(assessmentToSubmit, null, 2));
  
    try {
      await axios.post(`${apiUrl}/voice-assessments`, assessmentToSubmit, {
        headers: { "Content-Type": "application/json" },
      });
      console.log("✅ Dades enviades correctament.");
      onSubmit(assessmentToSubmit);
    } catch (error) {
      console.error("⚠️ Error en l'enviament de dades compartides:", error);
      if (error.response) {
        console.error("📩 Resposta del servidor:", error.response.data);
      }
    } finally {
      setIsSharingConsentModalOpen(false);
    }
  };  

  return (
    <form className="activity-form" onSubmit={handleSubmit}>

      <ResourceLanguageWarning resourceKey="CAPEVR" />

      <CAPEVrProtocolLink  />
      
      <div className="app-module-section">
        <Demographics
          subjectNameId={subjectNameId}
          setSubjectNameId={setSubjectNameId}
          subjectGender={subjectGender}
          setSubjectGender={setSubjectGender}
          subjectAge={subjectAge}
          setSubjectAge={setSubjectAge}
          examinerId={examinerId}
          setExaminerId={setExaminerId}
          recordingDate={recordingDate}
          setRecordingDate={setRecordingDate}
        />
      </div>
      
      <div className="app-module-section">
        <h2>{t('recording_conditions')}</h2>
        <RecordingConditions
          isAudioRecorded={isAudioRecorded}
          setIsAudioRecorded={setIsAudioRecorded}
          sessionModality={sessionModality}
          setSessionModality={setSessionModality}
          recordingEnvironment={recordingEnvironment}
          setRecordingEnvironment={setRecordingEnvironment}
          recordingSetup={recordingSetup}
          setRecordingSetup={setRecordingSetup}
          mouthToMicDistanceCm={mouthToMicDistanceCm}
          setMouthToMicDistanceCm={setMouthToMicDistanceCm}
        />
      </div>

      <div className="app-module-section">
        <h2>{t('stimuli')}</h2>
        <VocalTasks
          areSentencesModeled={areSentencesModeled}
          setAreSentencesModeled={setAreSentencesModeled}
          readingPassage={readingPassage} 
          setReadingPassage={setReadingPassage}
        />
      </div>

      <div className="app-module-section">
        <h2>{t('rating_conditions')}</h2>
        <RatingConditions
          voiceSource={voiceSource}
          setVoiceSource={setVoiceSource}
          listeningDevice={listeningDevice}
          setListeningDevice={setListeningDevice}
          auditoryAnchors={auditoryAnchors}
          setAuditoryAnchors={setAuditoryAnchors}
          raterId={raterId}
          setRaterId={setRaterId}
          ratingDate={ratingDate}
          setRatingDate={setRatingDate}
          timesPlayed={timesPlayed}
          setTimesPlayed={setTimesPlayed}
        />
      </div>

      <div className="app-module-section">
        {selectedSample && (
          <div className="app-module-audio-container">
            <h2>{t("sample")}</h2>
            {renderSample ? (
              renderSample()
            ) : selectedSample.sampleLink ? (
              <audio controls src={audioSrc}></audio>
            ) : (
              <div className="warning-message">
                <AVIcon icon="alert" size={36} color="#209385" />
                {t('no_audio_available')}
              </div>
            )}
          </div>
        )}
      </div>
      
      {!selectedSample && (
        <div className="app-btn-container app-module-section">
          <h2>{t("sample")}</h2>
          <button 
            type="button" 
            className="app-btn app-btn-primary"
            onClick={() => onSubmit({ goBackToSampleSelection: true })}
          >
            {t("choose_a_sample_to_evaluate")}
          </button>
        </div>
      )}

      <div className="app-module-section">
        <h2>{t('rating_form')}</h2>
        <CAPEVRRatingForm
          t={t}
          overallSeverity={overallSeverity}
          setOverallSeverity={setOverallSeverity}
          roughness={roughness}
          setRoughness={setRoughness}
          breathiness={breathiness}
          setBreathiness={setBreathiness}
          strain={strain}
          setStrain={setStrain}
          customVasLabel={customVasLabel}
          setCustomVasLabel={setCustomVasLabel}
          customVasValue={customVasValue}
          setCustomVasValue={setCustomVasValue}
          selectedInstabilities={selectedInstabilities}
          setSelectedInstabilities={setSelectedInstabilities}
          instabilitiesValues={instabilitiesValues}
          setInstabilitiesValues={setInstabilitiesValues}
          selectedAdditionalFeatures={selectedAdditionalFeatures}
          setSelectedAdditionalFeatures={setSelectedAdditionalFeatures}
          additionalFeaturesValues={additionalFeaturesValues}
          setAdditionalFeaturesValues={setAdditionalFeaturesValues}
          normalPitch={normalPitch}
          setNormalPitch={setNormalPitch}
          tooHighPitch={tooHighPitch}
          setTooHighPitch={setTooHighPitch}
          tooLowPitch={tooLowPitch}
          setTooLowPitch={setTooLowPitch}
          normalLoudness={normalLoudness}
          setNormalLoudness={setNormalLoudness}
          tooLoudLoudness={tooLoudLoudness}
          setTooLoudLoudness={setTooLoudLoudness}
          tooQuietLoudness={tooQuietLoudness}
          setTooQuietLoudness={setTooQuietLoudness}
          normalResonance={normalResonance}
          setNormalResonance={setNormalResonance}
          frontResonance={frontResonance}
          setFrontResonance={setFrontResonance}
          backResonance={backResonance}
          setBackResonance={setBackResonance}
          normalNasality={normalNasality}
          setNormalNasality={setNormalNasality}
          hypernasality={hypernasality}
          setHypernasality={setHypernasality}
          hyponasality={hyponasality}
          setHyponasality={setHyponasality}
          vowelsInconsistency={vowelsInconsistency}
          setVowelsInconsistency={setVowelsInconsistency}
          sentencesInconsistency={sentencesInconsistency}
          setSentencesInconsistency={setSentencesInconsistency}
          extemporaneousSpeechInconsistency={extemporaneousSpeechInconsistency}
          setExtemporaneousSpeechInconsistency={setExtemporaneousSpeechInconsistency}
          inconsistencyStatus={inconsistencyStatus}
          setInconsistencyStatus={setInconsistencyStatus}
          overallImpressionComment={overallImpressionComment}
          setOverallImpressionComment={setOverallImpressionComment}
        />
      </div>

        {validationSnapshot && (
          <div className="warning-message capev-validation-alert" role="alert">
            <AVIcon className="capev-validation-alert__icon" icon="alert" size={16} color="#209385" />
            <div className="capev-validation-alert__content">
              <span>{t('cape_vr_validation_summary')}</span>
              <ul>
                {validationSnapshot.messages.map(message => <li key={message}>{message}</li>)}
              </ul>
            </div>
          </div>
        )}

        <div className="app-btn-container">
          <button
            className="app-btn app-btn-primary"
            type="submit"
          >
            {t('submit')}
          </button>
        </div>

        {/* 🔹 SharingConsentModal per compartir dades */}
        <SharingConsentModal 
          isOpen={isSharingConsentModalOpen} 
          onRequestClose={() => setIsSharingConsentModalOpen(false)}
          onSubmit={handleConfirmSharing} 
          initialShareForFeedback={sociodemographicData?.wantsToShareEvaluations || false}
          initialResearchUsage={sociodemographicData?.acceptResearchUsage || false}
          voiceCompetenceLevel={sociodemographicData?.voiceCompetenceLevel}  
        />
    </form>
  );
}

export default CAPEVRForm;
